import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  orderBy,
  query,
} from 'firebase/firestore';
import { collectionData, docData } from 'rxfire/firestore';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import derivedDataKeys from '../../constants/derived_data_keys';
import dataKeys from '../../constants/force_data_keys';
import {
  IRawShardStringified,
  IRawTest,
  IRawTestStringified,
  IUser,
} from '../types';
import { authenticatedUserProvider } from './userProvider';

const rawTest = (testId: string, user: IUser): Observable<IRawTest> => {
  return docData(
    doc(
      user.services.firestore,
      `orgs/${user.orgId}/raw/${testId}`
    ) as DocumentReference<IRawTestStringified>,
    { idField: 'id' }
  ).pipe(
    map(
      (rawTestStringified: IRawTestStringified): IRawTest =>
        fromRawStringifiedToRawTest(rawTestStringified)
    ),
    mergeMap((parent: IRawTest): Observable<IRawTest> => {
      if (!parent.sharded) {
        return of(parent);
      }
      return collectionData(
        query(
          collection(
            user.services.firestore,
            `orgs/${user.orgId}/raw/${testId}/shards`
          ) as CollectionReference<IRawShardStringified>,
          orderBy('order', 'asc')
        )
      ).pipe(
        map((shards: IRawShardStringified[]): IRawTest => {
          return mergeShards(parent, shards);
        })
      );
    })
  );
};

export const rawProvider = (testId: string): Observable<IRawTest> => {
  return authenticatedUserProvider()
    .pipe(switchMap(rawTest.bind(null, testId)))
    .pipe(take(1));
};

/**
 * merges the parent document and its shards into a single document for processing
 * @param {Object} parent the parent raw document
 * @param {Array<Object>} shards an array of raw document shards
 * @returns {Object}
 */
const mergeShards = (
  parent: IRawTest,
  shards: IRawShardStringified[]
): IRawTest => {
  return shards.reduce(
    (acc: IRawTest, curr: IRawShardStringified): IRawTest => {
      [...dataKeys, ...derivedDataKeys].forEach((key: string): void => {
        if (curr.hasOwnProperty(key)) {
          const currentData =
            typeof acc[key] === 'string' ? JSON.parse(acc[key]) : acc[key];
          acc[key] = [].concat(
            currentData,
            JSON.parse(ensureParseableArray(curr[key]))
          );
        }
      });
      return acc;
    },
    parent
  );
};

const fromRawStringifiedToRawTest = (
  stringified: IRawTestStringified
): IRawTest => {
  const left: number[] = JSON.parse(ensureParseableArray(stringified.left));
  const right: number[] = JSON.parse(ensureParseableArray(stringified.right));
  const combined: number[] = JSON.parse(
    ensureParseableArray(stringified.combined)
  );
  const xForceLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.xForceLeft)
  );
  const xForceRight: number[] = JSON.parse(
    ensureParseableArray(stringified.xForceRight)
  );
  const yForceLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.yForceLeft)
  );
  const yForceRight: number[] = JSON.parse(
    ensureParseableArray(stringified.yForceRight)
  );
  const xMomentsLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.xMomentsLeft)
  );
  const xMomentsRight: number[] = JSON.parse(
    ensureParseableArray(stringified.xMomentsRight)
  );
  const yMomentsLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.yMomentsLeft)
  );
  const yMomentsRight: number[] = JSON.parse(
    ensureParseableArray(stringified.yMomentsRight)
  );
  const momentsRight: number[] = JSON.parse(
    ensureParseableArray(stringified.momentsRight)
  );
  const momentsLeft: number[] = JSON.parse(
    ensureParseableArray(stringified.momentsLeft)
  );
  const velocities: number[] = JSON.parse(
    ensureParseableArray(stringified.velocities)
  );
  const power: number[] = JSON.parse(ensureParseableArray(stringified.power));
  const displacements: number[] = JSON.parse(
    ensureParseableArray(stringified.displacements)
  );
  const rsi: number[] = JSON.parse(ensureParseableArray(stringified.rsi));
  // tslint:disable:object-literal-sort-keys
  return {
    ...stringified,
    left,
    right,
    combined,
    xForceLeft,
    xForceRight,
    yForceLeft,
    yForceRight,
    xMomentsLeft,
    xMomentsRight,
    yMomentsLeft,
    yMomentsRight,
    momentsLeft,
    momentsRight,
    velocities,
    power,
    displacements,
    rsi,
  };
  // tslint:enable
};

const ensureParseableArray = (value: string): string => {
  const parseableArray = '[]';
  if (value === undefined || value === null) return parseableArray;
  if (value === 'NA') return parseableArray;
  return value;
};
